import { useCallback } from 'react';

import { systemConfig } from '@/config/system.config';
import { logger } from '@/lib/logger';
import { login as apiLogin, logout as apiLogout } from '@/services/auth/auth.service';
import type { LoginResponse } from '@/services/auth/login-result.interface';
import { clearAppConfigCache } from '@/services/config/app-config.service';
import { useAppConfigStore } from '@/store/app-config.store';
import { useAuthStore } from '@/store/auth.store';
import type { User } from '@/types/common.types';

export interface UseAuthResult {
  token: string | null;
  user: User | null;
  tenantId: string | null;
  isAuthenticated: boolean;
  signIn: (idToken: string) => Promise<LoginResponse>;
  signOut: () => Promise<void>;
}

/**
 * Auth state and sign-in / sign-out actions (B2C token exchange and API logout).
 */
export function useAuth(): UseAuthResult {
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const tenantId = useAuthStore((state) => state.tenantId);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);

  const signIn = useCallback(
    async (idToken: string): Promise<LoginResponse> => {
      const result = await apiLogin(idToken);
      const tenantId = result.tenantId ?? systemConfig.tenantId;
      login(result.accessToken, result.user, tenantId);
      return result;
    },
    [login]
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await apiLogout();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      logger.warn('Logout API call failed, but proceeding with local logout', {
        error: errorMessage,
      });
    } finally {
      clearAppConfigCache();
      useAppConfigStore.getState().reset();
      logout();
    }
  }, [logout]);

  return {
    token,
    user,
    tenantId,
    isAuthenticated,
    signIn,
    signOut,
  };
}
